import html from './header.html';
import {partial} from '../../../utils/partial';
import {emitter, EVENTS} from '../../../utils/emitter';
import {uiContainer, phoneBoundary, getLanguage} from '../../../configs/global';
import {Window} from '../../../utils/controllers/window';
import {TweenMax, Power3, TimelineLite} from 'gsap';
import {Router} from '../../../utils/router';
import {state} from '../../../utils/state';
import {Footer} from '../footer/footer';
import homeData from '../../../configs/home';
import copyData from '../../../configs/copy';
import Mustache from 'mustache';

/**
 * This is being used as a singleton.
 * There's only one instanced created that is used across all screens.
 * The instance is created in main and passed to all views on init.
 */
export class Header {
  public element: HTMLElement;
  private contentEl: HTMLElement;
  private headerContainerEl: HTMLElement;
  private titleHeaderEl: HTMLElement;
  private storiesContainerEl: HTMLElement;
  private storyMenuEl: HTMLElement;
  private storyLinkLis: NodeListOf<HTMLElement>;
  private storyLinks: NodeListOf<HTMLElement>;
  private campaignTitles: HTMLElement;
  private campaignTitle: HTMLElement;
  private campaignSubtitle: HTMLElement;
  private aboutEl: HTMLElement;
  private aboutElements: NodeListOf<HTMLElement>;
  private hamburgerDesktopEl: HTMLElement;
  private hamburgerMobileEl: HTMLElement;
  private menuEl: HTMLElement;
  private menuContainerEl: HTMLElement;
  private footerContainerEl: HTMLElement;
  private prevScrollPosition: number;
  private footer: Footer;
  private videoContainer: HTMLElement;
  private videos: NodeListOf<HTMLElement>;

  private darkMode: boolean = true;
  private menuExpanded: boolean = false;
  private menuMinimized: boolean = false;
  private headerHeight: number;
  private headerMinHeight: number;
  private headerMinOffset: number;

  private languageDropdownContainer: HTMLElement;
  private languageDropdownTitle: HTMLElement;
  private languageDropdownLink: HTMLElement;
  private languageDropdownArrow: SVGElement;
  private languageDropdownItems: any[];

  constructor(footer: Footer) {
    const la = getLanguage();
    this.element = partial(Mustache.render(html, Object.assign({}, homeData[la], copyData[la])), 'header');
    this.footer = footer;
    this.render();

    this.contentEl = document.querySelector(`#content-container`);
    this.headerContainerEl = this.element.querySelector(`.header-container`);
    this.titleHeaderEl = this.element.querySelector(`.menu-list .title`);
    this.campaignTitles = this.element.querySelector(`.campaign-titles`);
    this.campaignTitle = this.element.querySelector(`.campaign-titles .title`);
    this.campaignSubtitle = this.element.querySelector(`.campaign-titles .subtitle`);
    this.storiesContainerEl = this.element.querySelector('.stories-container');
    this.storyMenuEl = this.element.querySelector('.stories');
    this.storyLinkLis = this.element.querySelectorAll(`.stories li`);
    this.storyLinks = this.element.querySelectorAll(`.stories a`);
    this.aboutEl = this.element.querySelector(`.about-link`);
    this.aboutElements = this.element.querySelectorAll(`.about-container a`);
    this.hamburgerDesktopEl = this.element.querySelector(`.hamburger-desktop`);
    this.hamburgerMobileEl = this.element.querySelector(`.hamburger-mobile`);
    this.menuContainerEl = this.element.querySelector(`.menu-container`);
    this.footerContainerEl = this.element.querySelector(`.footer-container`);
    this.menuEl = this.element.querySelector(`.menu`);
    this.videoContainer = this.element.querySelector(`.video-container`);
    this.videos = this.element.querySelectorAll(`.video-container video`);

    this.languageDropdownContainer = this.element.querySelector(`.lang-container`);
    this.languageDropdownLink = this.languageDropdownContainer.querySelector(`.lang-link`);
    this.languageDropdownArrow = this.languageDropdownContainer.querySelector(`.lang-link .arrow`);
    this.languageDropdownTitle = this.languageDropdownContainer.querySelector(`.lang-link__title`);
    this.languageDropdownItems = [...this.languageDropdownContainer.querySelectorAll(`.select li`)];

    this.initLanguageDropdown();

    this.addHandlers();
    this.presetAnimation();

    // TODO: Should this be in the asset loader?
    try {
      if ((document as any).fonts.ready) {
        (document as any).fonts.ready.then(fontFaceSet => {
          fontFaceSet.ready.then(() => {
            this.onResize();
          });
        });
      } else {
        this.onResize();
      }
    } catch (error) {
      this.onResize();
    }
  }

  private addHandlers() {
    this.aboutEl.addEventListener('click', this.onAboutClick);
    this.hamburgerMobileEl.addEventListener('click', this.onHamburgerMobileClick);
    this.hamburgerDesktopEl.addEventListener('click', this.onHamburgerDesktopClick);
    this.languageDropdownLink.addEventListener(`click`, this.onLanguageDropdownClick);
    this.languageDropdownItems.forEach(x => x.addEventListener('click', this.onLanguageDropdownItemClick));

    for (let el of this.storyLinkLis) {
      el.addEventListener('mousemove', this.onItemOverHandler.bind(this, el));
      el.addEventListener('mouseout', this.onItemOutHandler.bind(this, el));
    }

    emitter.on(EVENTS.resize, this.onResize);
  }

  private initLanguageDropdown() {
    this.languageDropdownItems.forEach(el => {
      if (el.dataset.lng === getLanguage()) {
        this.languageDropdownTitle.innerText = el.innerText;
        el.classList.add('active');
      }
    });
  }

  private onLanguageDropdownClick = event => {
    event.preventDefault();
    this.languageDropdownContainer.classList.toggle('open');
  };

  private onLanguageDropdownItemClick = event => {
    event.preventDefault();
    const local = event.target.parentElement.dataset.lng;
    localStorage.setItem('lang', local);
    location.reload();
  };

  private onItemOverHandler(el, e) {
    for (let video of this.videos) {
      if (video.dataset.id === el.dataset.id) video.classList.add('visible');
      else video.classList.remove('visible');
    }

    this.videoContainer.classList.add('visible');
    this.videoContainer.style.left = `${e.clientX - this.videoContainer.clientWidth / 2}px`;
    this.videoContainer.style.top = `${e.clientY - this.videoContainer.clientHeight / 2}px`;
  }

  private onItemOutHandler() {
    this.videoContainer.classList.remove('visible');
  }

  private onResize = () => {
    // Reset Header
    TweenMax.set(this.element, {clearProps: 'y'});
    TweenMax.set(this.storiesContainerEl, {clearProps: 'all'});
    TweenMax.set(this.contentEl, {clearProps: 'top'});
    TweenMax.set(this.campaignTitles, {clearProps: 'y'});
    this.hamburgerMobileEl.classList.remove('is-active');
    this.hamburgerDesktopEl.classList.remove('is-active');
    this.menuExpanded = false;
    this.menuMinimized = false;

    // Mobile minimized height
    const paddingTop = window.getComputedStyle(this.headerContainerEl, null).getPropertyValue('padding-top');

    this.headerMinHeight =
      this.campaignTitles.clientHeight + this.hamburgerMobileEl.clientHeight + parseInt(paddingTop);

    this.headerMinOffset = this.campaignTitles.clientHeight + parseInt(paddingTop);

    // Set menu to black for mobile.
    if (Window.width < phoneBoundary) {
      this.positionFooter('mobile');
      this.element.style.height = `${this.headerMinHeight}px`;
    } else {
      TweenMax.set(this.element, {y: 0, backgroundColor: 'rgba(255,255,255,0)'});
      this.positionFooter('desktop');
      this.element.style.height = 'auto';
    }

    // Total header height
    this.headerHeight = this.headerContainerEl.clientHeight;

    this.onScroll(window.scrollY);
  };

  private positionFooter(device: string) {
    let container: HTMLElement;
    let elInHeader: boolean;

    elInHeader = this.footerContainerEl.querySelector('.container') ? true : false;

    if (device === 'mobile' && !elInHeader) {
      container = this.footer.element.querySelector('.container');
      this.footerContainerEl.insertBefore(container, this.footerContainerEl.childNodes[0]);
    } else if (device === 'desktop' && elInHeader) {
      container = this.element.querySelector('.container');
      this.footer.element.appendChild(container);
    }
  }

  public onScroll = (scrollTop: number, bottomOfPage: number = 0) => {
    const timing = 0.5;
    const ease = Power3.easeOut;
    const phoneBounceThreshold = 100;

    this.prevScrollPosition || 0;

    if (phoneBoundary <= Window.width) {
      // Tuck desktop header away
      if (Window.halfHeight < scrollTop && !this.menuMinimized) {
        TweenMax.to(this.campaignTitles, timing, {y: -this.headerMinOffset, ease});
        this.menuMinimized = true;
      } else if (Window.halfHeight >= scrollTop && this.menuMinimized) {
        TweenMax.to(this.campaignTitles, timing, {y: 0, ease: Power3.easeOut});
        this.menuMinimized = false;
      }
    } else {
      // Tuck mobile header away
      if (this.prevScrollPosition < scrollTop && scrollTop > phoneBounceThreshold && !this.menuMinimized) {
        // Down - Hide titles
        this.element.classList.add('is-title-hidden');
        TweenMax.to(this.element, timing, {y: -this.headerMinOffset, ease});
        this.menuMinimized = true;
      } else if (this.prevScrollPosition > scrollTop && scrollTop < bottomOfPage && this.menuMinimized) {
        // Up - Show titles
        this.element.classList.remove('is-title-hidden');
        TweenMax.to(this.element, timing, {y: 0, ease: Power3.easeOut});
        this.menuMinimized = false;
      }

      // Close menu when scrolling up
      if (this.prevScrollPosition < scrollTop && this.menuExpanded) {
        this.closeMobileMenu();
      }
    }

    this.prevScrollPosition = scrollTop;
  };

  public changeBackgroundTransparency(transparency: number) {
    const timing = 0.5;
    TweenMax.to(this.element, timing, {backgroundColor: `rgba(255, 255, 255, ${transparency})`});
  }

  private render() {
    const parent = document.querySelector(uiContainer);
    parent.append(this.element);
  }

  private onAboutClick = e => {
    e.preventDefault();
    e.stopPropagation();
    if (state.getValue('view').name === 'about') {
      Router.route('/');
    } else {
      Router.route('/about');
    }
  };

  private onHamburgerDesktopClick = () => {
    if (this.hamburgerDesktopEl.classList.contains('is-active')) {
      this.closeDesktopMenu();
    } else {
      this.openDesktopMenu();
    }
  };

  private closeDesktopMenu() {
    const timing = 0.5;
    const ease = Power3.easeOut;

    this.hamburgerDesktopEl.classList.remove('is-active');

    TweenMax.to(this.storiesContainerEl, timing, {
      opacity: 0,
      ease,
      onComplete: () => {
        this.storiesContainerEl.style.display = 'none';
        this.viewResize();
      }
    });

    if (state.getValue('view').name === 'about') {
      this.animateToWhite();
    }
  }

  private openDesktopMenu() {
    const timing = 0.5;
    const ease = Power3.easeOut;

    this.hamburgerDesktopEl.classList.add('is-active');

    // Show header
    TweenMax.to(this.campaignTitles, timing, {y: 0, ease});

    // Setters
    TweenMax.set(this.storiesContainerEl, {display: 'flex', opacity: 0});
    TweenMax.set(this.storyLinks, {opacity: 0, y: 100});

    // Stagger in animation
    TweenMax.to(this.storiesContainerEl, timing, {opacity: 1, ease});
    var tl = new TimelineLite({delay: 0.2});
    tl.staggerTo(this.storyLinks, 0.8, {opacity: 1, y: 0, ease}, 0.1);

    // Colors
    this.hamburgerDesktopEl.classList.remove('is-inverted');
    this.animateToBlack();
  }

  private onHamburgerMobileClick = () => {
    if (this.hamburgerMobileEl.classList.contains('is-active')) {
      this.closeMobileMenu();
    } else {
      this.openMobileMenu();
    }
  };

  private viewResize() {
    if (state.getValue('view').name === 'story') {
      state.getValue('view').instance.currentStory.onResize();
    } else {
      state.getValue('view').instance.resize();
    }
  }

  private closeMobileMenu() {
    const timing = 0.5;
    const ease = Power3.easeOut;

    this.menuExpanded = false;

    TweenMax.to(this.element, timing, {height: this.headerMinHeight, ease});
    TweenMax.to(this.contentEl, timing, {
      top: '0',
      ease,
      onComplete: () => this.viewResize()
    });

    this.hamburgerMobileEl.classList.remove('is-active');
  }

  private openMobileMenu() {
    const timing = 0.5;
    const ease = Power3.easeOut;
    const view = state.getValue('view');
    this.menuExpanded = true;

    if (view.name === 'about') {
      this.animateToWhite();
      this.hamburgerMobileEl.classList.add('is-inverted');
    } else {
      this.hamburgerMobileEl.classList.remove('is-inverted');
      this.animateToBlack();
    }

    TweenMax.to(this.element, timing, {height: this.headerHeight, ease});

    if (this.menuMinimized) {
      TweenMax.to(this.contentEl, timing, {top: this.headerHeight - this.headerMinOffset, ease});
    } else {
      TweenMax.to(this.contentEl, timing, {top: this.headerHeight, ease});
    }

    this.hamburgerMobileEl.classList.add('is-active');
  }

  public presetAnimation() {
    TweenMax.set(this.element, {opacity: 0});
    this.element.style.display = `block`;
  }

  /**
   * Progress animation based on a 0-1 value
   * This is used in the scroll-based home render loop
   */
  public step(p: number) {
    const r = 1 - p;
    TweenMax.set(this.element, {opacity: p, y: -10 * r});
  }

  public animateIn(delay = 0) {
    this.element.style.display = 'block';
    return new Promise(resolve => {
      TweenMax.to(this.element, 0.2, {opacity: 1, delay, onComplete: resolve});
    });
  }

  public animateOut(delay = 0) {
    return new Promise(resolve => {
      TweenMax.to(this.element, 0.2, {
        opacity: 0,
        delay,
        onComplete: () => {
          resolve();
          this.element.style.display = `none`;
        }
      });
    });
  }

  public animateToBlack(delay = 0) {
    let duration = 0.5;
    let color = 'rgb(30,30,30)';

    if (!this.darkMode) {
      this.darkMode = true;
      TweenMax.to(this.titleHeaderEl, duration, {color, delay});
      TweenMax.to(this.storyMenuEl, duration, {color, delay});
      TweenMax.to(this.campaignSubtitle, duration, {color, delay});
      TweenMax.to(this.aboutElements, duration, {color, delay});
      this.hamburgerMobileEl.classList.remove('is-inverted');
      this.hamburgerDesktopEl.classList.remove('is-inverted');
      this.languageDropdownContainer.classList.remove('is-inverted');
    }
  }

  public animateToWhite(delay = 0) {
    let duration = 0.5;
    let color = 'rgb(255,255,255)';

    if (this.darkMode) {
      this.darkMode = false;
      TweenMax.to(this.titleHeaderEl, duration, {color, delay});
      TweenMax.to(this.storyMenuEl, duration, {color, delay});
      TweenMax.to(this.campaignSubtitle, duration, {color, delay});
      TweenMax.to(this.aboutElements, duration, {color, delay});
      this.hamburgerMobileEl.classList.add('is-inverted');
      this.hamburgerDesktopEl.classList.add('is-inverted');
      this.languageDropdownContainer.classList.add('is-inverted');
    }
  }

  public updateState() {
    const activeStoryMenuEl = this.storyMenuEl.querySelector('.active');
    if (activeStoryMenuEl) {
      activeStoryMenuEl.classList.remove('active');
    }
    this.titleHeaderEl.classList.remove('active');

    const view = state.getValue('view');
    if (view.name == 'story' && view.instance.currentStory) {
      console.log(view, view.instance);
      console.log(view.instance.currentStory.id);
      this.storyMenuEl.querySelector('.' + view.instance.currentStory.id).classList.add('active');
    } else if (view.name == 'home') {
      this.titleHeaderEl.classList.add('active');
    }

    if (this.hamburgerDesktopEl.classList.contains('is-active')) this.closeDesktopMenu();

    if (this.hamburgerMobileEl.classList.contains('is-active')) this.closeMobileMenu();
  }

  public aboutLinkActive(active: boolean) {
    active ? this.aboutEl.classList.add('active') : this.aboutEl.classList.remove('active');
  }

  public disableInteraction() {
    this.element.style.pointerEvents = `none`;
  }

  public enableInteraction() {
    this.element.style.pointerEvents = `all`;
  }

  public show() {
    TweenMax.set(this.element, {opacity: 1});
    this.element.style.display = `block`;
  }

  public hide() {
    TweenMax.set(this.element, {opacity: 0});
    this.element.style.display = `none`;
  }
}
